/**
 * Live source-selection persistence. Two distinct selections live here:
 *
 * - The SHARED selection (`fleetPerformanceSources` + filterStore.selectedSources) — what every
 *   settings menu in the app writes (PerfSettings, PageSettings, ManeuverSettings, RaceSettings,
 *   TimeSeriesSettings, FleetPerformanceHistory) and what the report pages filter by.
 * - The LIVE MAP's own selection (`liveMapSources`) — the boat toggles in the live map's settings
 *   panel. Deliberately NOT the shared one: picking three boats to watch on the live map used to
 *   re-select those same three on every report page, and vice versa.
 *
 * Both live in the same flat user-settings blob (persistentSettingsService merges shallowly at the
 * top level, so they must be sibling keys, never nested).
 */

import { apiEndpoints } from '../config/env';
import { persistentSettingsService } from '../services/persistentSettingsService';
import { persistantStore } from '../store/persistantStore';
import { sourcesStore } from '../store/sourcesStore';
import { setSelectedSources } from '../store/filterStore';
import { user } from '../store/userStore';
import { getData } from './global';
import { debug } from './console';
import { initializeSourceSelections } from './sourceInitialization';

const LEGACY_LIVE_SOURCES_KEY = 'live_sources';
const LEGACY_LIVE_SOURCES_PARENT = 'live';

function legacyStoreKey(className: string, projectId: number | string): string {
  return `${className}_${projectId}_live`;
}

/** Map source IDs to names via sourcesStore (skips unknown IDs). */
export function sourceNamesFromIds(ids: Set<number>): string[] {
  const names: string[] = [];
  for (const id of ids) {
    const name = sourcesStore.getSourceName(id);
    if (name) names.push(name);
  }
  return names;
}

/** Map source names to IDs, validating against current project sources. */
export function sourceIdsFromNames(names: string[]): Set<number> {
  const ids = new Set<number>();
  for (const name of names) {
    const trimmed = String(name).trim();
    if (!trimmed) continue;
    const id = sourcesStore.getSourceId(trimmed);
    if (id != null && Number.isFinite(id)) ids.add(id);
  }
  return ids;
}

/** Wait until user + project context are available (needed before reading saved settings). */
async function waitForRestoreContext(maxMs = 5000): Promise<boolean> {
  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    const currentUser = user();
    const { selectedClassName, selectedProjectId } = persistantStore;
    const className = selectedClassName?.() ?? '';
    const projectId = selectedProjectId?.();
    if (currentUser?.user_id && className && projectId != null) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return false;
}

function parseFleetPerformanceSourceNames(raw: unknown): string[] {
  if (!raw) return [];
  let sources = raw;
  if (typeof sources === 'string') {
    try {
      sources = JSON.parse(sources);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(sources)) return [];
  return sources
    .map((s) => String(s).trim())
    .filter((s) => s.length > 0);
}

/** Read legacy live_sources from localStorage. Exported for tests. */
export function getLegacyStoredLiveSourceIds(
  className: string,
  projectId: number | string
): number[] | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null;
  try {
    let raw = localStorage.getItem(LEGACY_LIVE_SOURCES_KEY);
    if (!raw) {
      const older = localStorage.getItem('liveMapSelectedSourceIds');
      if (older) raw = older;
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const key = legacyStoreKey(className, projectId);
    const ids = (parsed as Record<string, unknown>)[key];
    if (!Array.isArray(ids) || ids.length === 0) return null;
    return ids
      .map((id) => Number(id))
      .filter((id): id is number => Number.isFinite(id));
  } catch {
    return null;
  }
}

/** Load legacy live_sources from /users/object. */
async function loadLegacyLiveSourcesFromUserObject(): Promise<number[] | null> {
  const { selectedClassName, selectedProjectId } = persistantStore;
  const cls = selectedClassName?.() ?? '';
  const proj = selectedProjectId?.() ?? 0;
  const currentUser = user();
  if (!cls || proj == null || !currentUser?.user_id) return null;
  try {
    const url = `${apiEndpoints.app.users}/object?class_name=${encodeURIComponent(cls)}&project_id=${encodeURIComponent(proj)}&user_id=${encodeURIComponent(currentUser.user_id)}&parent_name=${encodeURIComponent(LEGACY_LIVE_SOURCES_PARENT)}&object_name=${encodeURIComponent(LEGACY_LIVE_SOURCES_KEY)}`;
    const response = await getData(url);
    if (!response.success || response.data == null) return null;
    const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    const ids = data?.source_ids;
    if (!Array.isArray(ids) || ids.length === 0) return null;
    return ids
      .map((id: unknown) => Number(id))
      .filter((id): id is number => Number.isFinite(id));
  } catch {
    return null;
  }
}

async function loadFleetPerformanceSourceNames(): Promise<string[] | null> {
  const currentUser = user();
  if (!currentUser?.user_id) return null;
  const { selectedClassName, selectedProjectId } = persistantStore;
  const className = selectedClassName();
  const projectId = selectedProjectId();
  if (!className || projectId == null) return null;
  try {
    const settings = await persistentSettingsService.loadSettings(
      currentUser.user_id,
      className,
      projectId
    );
    const names = parseFleetPerformanceSourceNames(settings?.fleetPerformanceSources);
    return names.length > 0 ? names : null;
  } catch (err) {
    debug('[liveSourcePersistence] Error loading fleetPerformanceSources', err);
    return null;
  }
}

async function migrateLegacyToFleetSettings(ids: number[]): Promise<Set<number> | null> {
  const restored = sourceIdsFromNames(sourceNamesFromIds(new Set(ids)));
  if (restored.size === 0) return null;
  const names = sourceNamesFromIds(restored);
  if (names.length === 0) return null;

  const currentUser = user();
  const { selectedClassName, selectedProjectId } = persistantStore;
  const className = selectedClassName();
  const projectId = selectedProjectId();
  if (!currentUser?.user_id || !className || projectId == null) return restored;

  try {
    await persistentSettingsService.saveSettings(
      currentUser.user_id,
      className,
      projectId,
      { fleetPerformanceSources: names }
    );
    setSelectedSources(names);
    debug('[liveSourcePersistence] Migrated legacy live sources to fleetPerformanceSources', {
      count: names.length,
    });
  } catch (err) {
    debug('[liveSourcePersistence] Legacy migration save failed', err);
  }
  return restored;
}

/**
 * Load the SHARED source selection (the one the report pages use).
 * Priority: fleetPerformanceSources → legacy uplift → initializeSourceSelections default.
 */
export async function loadSharedSourceIds(): Promise<Set<number>> {
  if (!sourcesStore.isReady()) {
    let attempts = 0;
    while (!sourcesStore.isReady() && attempts < 10) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }
  }
  if (!sourcesStore.isReady() || sourcesStore.sources().length === 0) {
    return new Set();
  }

  await waitForRestoreContext();

  const fleetNames = await loadFleetPerformanceSourceNames();
  if (fleetNames) {
    const ids = sourceIdsFromNames(fleetNames);
    if (ids.size > 0) {
      setSelectedSources(fleetNames);
      debug('[liveSourcePersistence] Loaded from fleetPerformanceSources', { count: ids.size });
      return ids;
    }
  }

  const { selectedClassName, selectedProjectId } = persistantStore;
  const className = selectedClassName?.() ?? '';
  const projectId = selectedProjectId?.() ?? 0;

  const legacyFromObject = await loadLegacyLiveSourcesFromUserObject();
  if (legacyFromObject && legacyFromObject.length > 0) {
    const migrated = await migrateLegacyToFleetSettings(legacyFromObject);
    if (migrated && migrated.size > 0) {
      debug('[liveSourcePersistence] Restored from legacy user object', { count: migrated.size });
      return migrated;
    }
  }

  if (className) {
    const legacyFromLs = getLegacyStoredLiveSourceIds(className, projectId);
    if (legacyFromLs && legacyFromLs.length > 0) {
      const migrated = await migrateLegacyToFleetSettings(legacyFromLs);
      if (migrated && migrated.size > 0) {
        debug('[liveSourcePersistence] Restored from legacy localStorage', { count: migrated.size });
        return migrated;
      }
    }
  }

  const defaultNames = await initializeSourceSelections();
  const defaultIds = sourceIdsFromNames(defaultNames);
  if (defaultIds.size > 0) {
    debug('[liveSourcePersistence] Using default source selection', { count: defaultIds.size });
    return defaultIds;
  }

  return new Set();
}

/** Persist the SHARED source selection to fleetPerformanceSources and sync filterStore. */
export async function saveSharedSourceIds(ids: Set<number>): Promise<void> {
  if (ids.size === 0) return;

  const names = sourceNamesFromIds(ids);
  if (names.length === 0) return;

  setSelectedSources(names);

  const currentUser = user();
  const { selectedClassName, selectedProjectId } = persistantStore;
  const className = selectedClassName?.() ?? '';
  const projectId = selectedProjectId?.() ?? 0;
  if (!currentUser?.user_id || !className) return;

  try {
    await persistentSettingsService.saveSettings(
      currentUser.user_id,
      className,
      projectId,
      { fleetPerformanceSources: names }
    );
    debug('[liveSourcePersistence] Saved fleetPerformanceSources', { count: names.length });
  } catch (err) {
    debug('[liveSourcePersistence] Error saving fleetPerformanceSources', err);
  }
}

// ---------------------------------------------------------------------------------------------
// Live map's own selection
// ---------------------------------------------------------------------------------------------

const LIVE_MAP_SOURCES_KEY = 'liveMapSources';

async function loadLiveMapSourceNames(): Promise<string[] | null> {
  const currentUser = user();
  if (!currentUser?.user_id) return null;
  const { selectedClassName, selectedProjectId } = persistantStore;
  const className = selectedClassName();
  const projectId = selectedProjectId();
  if (!className || projectId == null) return null;
  try {
    const settings = await persistentSettingsService.loadSettings(
      currentUser.user_id,
      className,
      projectId
    );
    const names = parseFleetPerformanceSourceNames(settings?.[LIVE_MAP_SOURCES_KEY]);
    return names.length > 0 ? names : null;
  } catch (err) {
    debug('[liveSourcePersistence] Error loading liveMapSources', err);
    return null;
  }
}

/**
 * Load the live map's OWN source selection.
 *
 * First visit has no `liveMapSources` key, so it seeds from the shared selection and writes the
 * seed straight back under its own key — otherwise the map would silently re-inherit whatever a
 * report page last selected on every reload, right up until the first toggle.
 *
 * Never writes filterStore: that is the point of the split.
 */
export async function loadLiveMapSourceIds(): Promise<Set<number>> {
  if (!sourcesStore.isReady()) {
    let attempts = 0;
    while (!sourcesStore.isReady() && attempts < 10) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }
  }
  if (!sourcesStore.isReady() || sourcesStore.sources().length === 0) {
    return new Set();
  }

  await waitForRestoreContext();

  const ownNames = await loadLiveMapSourceNames();
  if (ownNames) {
    const ids = sourceIdsFromNames(ownNames);
    if (ids.size > 0) {
      debug('[liveSourcePersistence] Loaded live map selection', { count: ids.size });
      return ids;
    }
  }

  // Seed once from the shared selection (which itself falls back through the legacy stores and
  // initializeSourceSelections), then claim it as the map's own.
  const seed = await loadSharedSourceIds();
  if (seed.size > 0) {
    await saveLiveMapSourceIds(seed);
    debug('[liveSourcePersistence] Seeded live map selection from shared sources', {
      count: seed.size,
    });
  }
  return seed;
}

/**
 * Persist the live map's own source selection. Unlike saveSharedSourceIds this does NOT touch
 * filterStore or fleetPerformanceSources, so toggling boats on the live map leaves the report
 * pages alone.
 */
export async function saveLiveMapSourceIds(ids: Set<number>): Promise<void> {
  if (ids.size === 0) return;

  const names = sourceNamesFromIds(ids);
  if (names.length === 0) return;

  const currentUser = user();
  const { selectedClassName, selectedProjectId } = persistantStore;
  const className = selectedClassName?.() ?? '';
  const projectId = selectedProjectId?.() ?? 0;
  if (!currentUser?.user_id || !className) return;

  try {
    await persistentSettingsService.saveSettings(
      currentUser.user_id,
      className,
      projectId,
      { [LIVE_MAP_SOURCES_KEY]: names }
    );
    debug('[liveSourcePersistence] Saved liveMapSources', { count: names.length });
  } catch (err) {
    debug('[liveSourcePersistence] Error saving liveMapSources', err);
  }
}
